import { storageKeyWithSuffix } from '@/lib/config/brand'
import {
  captureDocumentScrollRatio,
  restoreDocumentScrollRatio,
} from '@/lib/document-scroll-restore'

export type ReadingPosition = {
  ratio: number
  updatedAt: number
}

function positionKey(documentId: string): string {
  return storageKeyWithSuffix('reading_position', documentId)
}

export function getReadingPosition(documentId: string | null | undefined): ReadingPosition | null {
  if (!documentId || typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(positionKey(documentId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as ReadingPosition
    if (typeof parsed?.ratio !== 'number' || !Number.isFinite(parsed.ratio)) return null
    return {
      ratio: Math.max(0, Math.min(1, parsed.ratio)),
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    }
  } catch {
    return null
  }
}

export function setReadingPosition(documentId: string, ratio: number): void {
  if (!documentId || typeof sessionStorage === 'undefined') return
  const clamped = Math.max(0, Math.min(1, ratio))
  try {
    sessionStorage.setItem(
      positionKey(documentId),
      JSON.stringify({ ratio: clamped, updatedAt: Date.now() } satisfies ReadingPosition)
    )
  } catch {
    // quota / private mode
  }
}

/** Debounced persist of scroll ratio for the shared document scroller. */
export function bindReadingPositionPersistence(
  documentId: string | null | undefined,
  scrollRoot: ParentNode | null | undefined,
  debounceMs = 200
): () => void {
  if (!documentId || !scrollRoot || typeof window === 'undefined') return () => {}

  let timer: ReturnType<typeof setTimeout> | null = null
  const onScroll = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      const ratio = captureDocumentScrollRatio(scrollRoot)
      if (ratio != null) setReadingPosition(documentId, ratio)
    }, debounceMs)
  }

  const el =
    scrollRoot instanceof HTMLElement && scrollRoot.hasAttribute('data-scroll-container')
      ? scrollRoot
      : (scrollRoot as ParentNode).querySelector?.('[data-scroll-container]')
  if (!(el instanceof HTMLElement)) return () => {}

  el.addEventListener('scroll', onScroll, { passive: true })
  return () => {
    el.removeEventListener('scroll', onScroll)
    if (timer) clearTimeout(timer)
  }
}

/** Restore saved reading position after load/remount. */
export function restoreReadingPosition(
  documentId: string | null | undefined,
  scrollRoot: ParentNode | null | undefined
): boolean {
  const saved = getReadingPosition(documentId)
  if (!saved || !scrollRoot) return false
  restoreDocumentScrollRatio(scrollRoot, saved.ratio)
  return true
}
